import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getApiEndpoint } from "../../config/config";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import PageContent from "../shared/PageContent/PageContent";
import { formatPhoneNumber } from "../../utils/formatters";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";
import {
  TextInput,
  Button,
  Group,
  Stack,
  Switch,
  Select,
  Textarea,
  Grid,
  Paper,
  Title,
  ActionIcon,
  Modal,
  Text,
  Collapse,
  Box,
  Container,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconCloudUpload,
  IconX,
} from "@tabler/icons-react";
import AddBonusForm from "../Organization/AddBonusForm";
import AddExpenseForm from "../Organization/AddExpenseForm";
import AddUpfrontPayForm from "../Organization/AddUpfrontPayForm";
import {
  BONUS_CATEGORY_LABELS,
  EXPENSE_CATEGORY_LABELS,
  UPFRONT_PAY_TYPE_LABELS,
  titleize,
  toScaleLines,
} from "./repDealDisplay";
import {
  ItemRow,
  ItemTable,
  ScaleTable,
  bonusFields,
  expenseFields,
  normalizeScaleLines,
  upfrontFields,
} from "./payEditorPieces";
import { nextEditorItemId } from "../../services/payExtraction";

// Mirrors the iOS EnhancedAddRepView: one scrollable page with a "Personal
// Information" card, a collapsed Optional Details section (web-only fields the
// Recruiting flow prefills), and a "Pay" card built from the same editable
// scale/item tables as the Send Offer flow. Submits to POST /v1/rep/add-rep.

const emptyScale = () => [{ revenue_amount: 0, percentage: 0 }];

// A scale table's lines → the AddRepDto's [{revenue, percentage}] shape: only
// rows with a percentage count, sorted, with the base row's revenue forced to 0
// — mirrors the iOS AddRepPayStore.scaleRequest.
const scalePayload = lines =>
  normalizeScaleLines(lines)
    .filter(line => Number(line.percentage) > 0)
    .map((line, i) => ({
      revenue: i === 0 ? 0 : Number(line.revenue_amount) || 0,
      percentage: Number(line.percentage) || 0,
    }));

// Drop local-only `_`-prefixed keys (list ids) before sending — the API
// validates with a whitelist, so an unknown field is a 400.
const stripLocal = item => {
  const out = {};
  Object.keys(item || {}).forEach(key => {
    if (!key.startsWith("_")) out[key] = item[key];
  });
  return out;
};

function AddRepPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess } = useToast();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [repTypes, setRepTypes] = useState([]);

  // Default scale templates
  const [commissionTemplates, setCommissionTemplates] = useState([]);
  const [overrideTemplates, setOverrideTemplates] = useState([]);
  const [marketingTemplates, setMarketingTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState({
    commission: "",
    override: "",
    md: "",
  });

  // Recruiting sources
  const [recruitingSources, setRecruitingSources] = useState([]);

  // Seasons — hidden from the UI; kept only as the fallback when the selected
  // rep type carries no season_id (see handleSave).
  const [seasons, setSeasons] = useState([]);

  // Pay-structure overview feeds the pay editor's type options (upfront /
  // bonus / expense), same source the Send Offer flow uses.
  const [overview, setOverview] = useState(null);

  const [repData, setRepData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    birthday: "",
    instagram: "",
    linkedin: "",
    recruitedBy: "",
    manager: "",
    isManager: false,
    repRank: "",
    recruitingSource: "",
    notes: "",
  });

  // Full offer-style pay, mirroring the iOS AddRepPayStore.
  const [pay, setPay] = useState({
    commissionLines: emptyScale(),
    allowOverride: false,
    overrideLines: emptyScale(),
    hasMarketingDeal: false,
    mdLines: emptyScale(),
    upfront: null,
    bonuses: [],
    expenses: [],
  });
  const [addTarget, setAddTarget] = useState(null);

  // Documents chosen before the rep exists; uploaded to the new rep's profile
  // right after POST /v1/rep/add-rep succeeds.
  const [docFiles, setDocFiles] = useState([]);
  const [docDragActive, setDocDragActive] = useState(false);

  const patchPay = next => setPay(prev => ({ ...prev, ...next }));

  useEffect(() => {
    loadRepsAndDefaults();
    loadDefaultScaleTemplates();
    loadRepTypes();
    loadRecruitingSources();
    loadSeasons();
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set default rep type to first one when rep types are loaded
  useEffect(() => {
    if (repTypes.length > 0 && !repData.repRank) {
      setRepData(prev => ({
        ...prev,
        repRank: repTypes[0].id.toString(),
      }));
    }
  }, [repTypes, repData.repRank]);

  // Populate form with lead data if coming from recruiting page
  useEffect(() => {
    const leadData = location.state?.leadData;
    if (leadData) {
      setRepData(prev => ({
        ...prev,
        firstName: leadData.firstName || "",
        lastName: leadData.lastName || "",
        email: leadData.email || "",
        phoneNumber: formatPhoneNumber(leadData.phoneNumber || ""),
        recruitedBy: leadData.recruitedBy || "",
        instagram: leadData.instagram || "",
        linkedin: leadData.linkedinProfile || "",
        recruitingSource: leadData.recruitingSourceId || "",
        manager: leadData.manager || "",
      }));
    }
  }, [location.state]);

  // Loads the rep dropdown data plus the current user's profile, then defaults
  // Recruited By and Manager to the current user's rep_id (iOS behavior). The
  // defaults never overwrite values already set (e.g. lead prefill), and the
  // manager default only applies when the current user is actually a manager.
  const loadRepsAndDefaults = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      };
      const [repsResponse, profileResponse] = await Promise.all([
        fetch(getApiEndpoint("/v1/rep/reps-list"), { method: "GET", headers }),
        fetch(getApiEndpoint("/v1/user/profile"), { method: "GET", headers }),
      ]);

      let managersList = [];
      if (repsResponse.ok) {
        const result = await repsResponse.json();
        const data = result.data;
        const allUsers = data.users || [];
        managersList =
          data.managers || allUsers.filter(rep => rep.is_manager === true);
        setUsers(allUsers);
        setManagers(managersList);
      }

      if (profileResponse.ok) {
        const result = await profileResponse.json();
        const repId = result.data?.rep_id;
        if (repId != null) {
          const isManagerRep = managersList.some(
            manager => String(manager.rep_id) === String(repId)
          );
          setRepData(prev => ({
            ...prev,
            recruitedBy: prev.recruitedBy || String(repId),
            manager: prev.manager || (isManagerRep ? String(repId) : ""),
          }));
        }
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const loadDefaultScaleTemplates = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      };
      const load = async (type, setter) => {
        const response = await fetch(
          getApiEndpoint(`/v1/scales/default-scales/${type}`),
          { method: "GET", headers }
        );
        if (response.ok) {
          const result = await response.json();
          setter(result.data || []);
        }
      };
      await Promise.all([
        load("COMMISSION", setCommissionTemplates),
        load("OVERRIDE", setOverrideTemplates),
        load("MD", setMarketingTemplates),
      ]);
    } catch (err) {
      // Silently handle error - templates are optional
    }
  };

  const loadRepTypes = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v2/company/rep-types"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setRepTypes(result.data || []);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const loadRecruitingSources = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint("/v2/company/recruiting-sources"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setRecruitingSources(result.data || []);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const loadSeasons = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/seasons"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setSeasons(result.data || []);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const loadOverview = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/ps/overview"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setOverview(result.data || null);
      }
    } catch (err) {
      // Silently handle error - pay type options just stay empty
    }
  };

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;

    if (name === "phoneNumber") {
      const formattedValue = formatPhoneNumber(value);
      setRepData(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setRepData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // Applying a default scale template fills the matching scale table.
  const applyTemplate = (templates, linesKey, templateKey) => value => {
    setSelectedTemplates(prev => ({ ...prev, [templateKey]: value || "" }));
    if (!value) return;
    const template = templates.find(t => t.id === parseInt(value));
    if (template && template.scale_lines) {
      patchPay({ [linesKey]: toScaleLines(template.scale_lines) });
    }
  };

  // Email validation function
  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // iOS-style single-message validation, checked in field order. Recruited By
  // and Manager stay optional for COMPANY_ADMIN (existing web behavior — an
  // admin may not be a rep themselves).
  const validateForm = () => {
    const { firstName, lastName, email, phoneNumber, recruitedBy, manager } =
      repData;
    const isCompanyAdmin = user?.role === "COMPANY_ADMIN";

    if (!firstName.trim()) return "First name is required";
    if (!lastName.trim()) return "Last name is required";
    if (!email.trim()) return "Email is required";
    if (!isValidEmail(email.trim())) {
      return "Please enter a valid email address";
    }
    if (!isCompanyAdmin && !recruitedBy) {
      return "Please select who recruited this rep";
    }
    if (!isCompanyAdmin && !manager) return "Please select a manager";
    if (!repData.repRank) return "Please select a rep rank";

    // Validate phone number format if provided (optional)
    if (phoneNumber && phoneNumber.trim()) {
      const cleanPhone = phoneNumber.replace(/\D/g, "");
      if (cleanPhone.length !== 10 && cleanPhone.length !== 11) {
        return "Please enter a valid phone number (10-11 digits)";
      }
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // One season entry derived from the selected rep type's season, falling
      // back to the company's first season — mirrors iOS, no visible section.
      const selectedRepType = repTypes.find(
        repType => String(repType.id) === String(repData.repRank)
      );
      const seasonId = selectedRepType?.season_id ?? seasons[0]?.id ?? null;

      const apiData = {
        first_name: repData.firstName,
        last_name: repData.lastName,
        email: repData.email.trim(),
        phone_number: repData.phoneNumber
          ? repData.phoneNumber.replace(/\D/g, "")
          : null,
        birthday: repData.birthday || null,
        instagram_username: repData.instagram || null,
        linkedin_profile: repData.linkedin || null,
        recruited_by: repData.recruitedBy
          ? parseInt(repData.recruitedBy)
          : null,
        manager: repData.manager ? parseInt(repData.manager) : null,
        is_manager: repData.isManager,
        rep_type_id: repData.repRank ? parseInt(repData.repRank) : null,
        recruiting_source_id: repData.recruitingSource
          ? parseInt(repData.recruitingSource)
          : null,
        commission_scale: scalePayload(pay.commissionLines),
        has_override: pay.allowOverride,
        override_scale: pay.allowOverride
          ? scalePayload(pay.overrideLines)
          : [],
        has_marketing_deal: pay.hasMarketingDeal,
        marketing_deal_scale: pay.hasMarketingDeal
          ? scalePayload(pay.mdLines)
          : [],
        notes: repData.notes,
        seasons: seasonId != null ? [{ season_id: seasonId }] : [],
        ...(pay.upfront ? { upfront_pay: stripLocal(pay.upfront) } : {}),
        ...(pay.bonuses.length > 0
          ? { bonuses: pay.bonuses.map(stripLocal) }
          : {}),
        ...(pay.expenses.length > 0
          ? { expenses: pay.expenses.map(stripLocal) }
          : {}),
      };

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(getApiEndpoint("/v1/rep/add-rep"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        // Upload any documents chosen in the form onto the new rep's profile.
        if (docFiles.length > 0) {
          const result = await response.json().catch(() => ({}));
          const newRepId = result?.data?.rep_id;
          const failed = [];
          if (newRepId) {
            for (const file of docFiles) {
              const formData = new FormData();
              formData.append("file", file);
              formData.append("label", file.name);
              try {
                const uploadResponse = await fetch(
                  getApiEndpoint(`/v1/rep/${newRepId}/documents/upload`),
                  {
                    method: "POST",
                    headers: { Authorization: `Bearer ${jwt}` },
                    body: formData,
                  }
                );
                if (!uploadResponse.ok) failed.push(file.name);
              } catch {
                failed.push(file.name);
              }
            }
          } else {
            failed.push(...docFiles.map(f => f.name));
          }
          if (failed.length > 0) {
            showSuccess(
              `Rep added, but some documents didn't upload: ${failed.join(", ")}. You can re-upload them from the rep's profile.`
            );
          } else {
            showSuccess("Rep added and documents uploaded!");
          }
        } else {
          showSuccess("Rep added successfully!");
        }
        navigate("/reps/downline");
      } else {
        // Try to parse the error response for specific error messages
        let errorMessage = "Failed to create rep";
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }

          // Check for duplicate email error patterns
          if (
            errorMessage.toLowerCase().includes("email") &&
            (errorMessage.toLowerCase().includes("exists") ||
              errorMessage.toLowerCase().includes("duplicate") ||
              errorMessage.toLowerCase().includes("already"))
          ) {
            errorMessage = "Rep with email already exists";
          }
        } catch (parseError) {
          // If we can't parse the response, use the default error message
        }

        throw new Error(errorMessage);
      }
    } catch (err) {
      setError(err.message || "Failed to save rep");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    setShowCancelConfirmation(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirmation(false);
    navigate("/reps/downline");
  };

  const handleCancelConfirmation = () => {
    setShowCancelConfirmation(false);
  };

  // A labeled toggle row matching the iOS pay card's switches.
  const toggleRow = (label, checked, onChange) => (
    <Group justify="space-between" align="center">
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <Switch
        checked={checked}
        onChange={event => onChange(event.currentTarget.checked)}
        disabled={loading}
        size="md"
      />
    </Group>
  );

  const templateSelect = (label, templates, selected, onChange) =>
    templates.length > 0 ? (
      <Select
        label={label}
        placeholder="Select a template..."
        data={templates.map(template => ({
          value: template.id.toString(),
          label: template.name,
        }))}
        value={selected}
        onChange={onChange}
        disabled={loading}
        clearable
        size="md"
      />
    ) : null;

  return (
    <PageContent
      breadcrumbs={[
        {
          label: "Downline Management",
          path: "/reps/downline",
        },
        {
          label: "Add New Rep",
          path: "",
        },
      ]}
      title="Add New Rep"
    >
      <Box
        style={{
          minHeight: "calc(100vh - 200px)",
          padding: "24px 0",
        }}
      >
        <Container size="md">
          <Stack gap="lg">
            {/* Card 1: Personal Information */}
            <Paper shadow="sm" p="md" radius="md">
              <Stack gap="md">
                <Title order={3}>Personal Information</Title>

                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="First Name"
                      placeholder="Enter first name"
                      value={repData.firstName}
                      onChange={event =>
                        handleInputChange({
                          target: {
                            name: "firstName",
                            value: event.currentTarget.value,
                          },
                        })
                      }
                      required
                      disabled={loading}
                      size="md"
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Last Name"
                      placeholder="Enter last name"
                      value={repData.lastName}
                      onChange={event =>
                        handleInputChange({
                          target: {
                            name: "lastName",
                            value: event.currentTarget.value,
                          },
                        })
                      }
                      required
                      disabled={loading}
                      size="md"
                    />
                  </Grid.Col>
                </Grid>

                <TextInput
                  type="email"
                  label="Email"
                  placeholder="Enter email address"
                  value={repData.email}
                  onChange={event =>
                    handleInputChange({
                      target: {
                        name: "email",
                        value: event.currentTarget.value,
                      },
                    })
                  }
                  required
                  disabled={loading}
                  size="md"
                />

                <Select
                  label="Recruited By"
                  placeholder="Select recruiter..."
                  data={users.map(rep => ({
                    value: String(rep.rep_id),
                    label: `${rep.first_name} ${rep.last_name}`,
                  }))}
                  value={repData.recruitedBy}
                  onChange={value =>
                    handleInputChange({
                      target: { name: "recruitedBy", value: value || "" },
                    })
                  }
                  required={user?.role !== "COMPANY_ADMIN"}
                  disabled={loading}
                  searchable
                  size="md"
                />

                <Select
                  label="Manager"
                  placeholder="Select manager..."
                  data={managers.map(manager => ({
                    value: String(manager.rep_id),
                    label: `${manager.first_name} ${manager.last_name}`,
                  }))}
                  value={repData.manager}
                  onChange={value =>
                    handleInputChange({
                      target: { name: "manager", value: value || "" },
                    })
                  }
                  required={user?.role !== "COMPANY_ADMIN"}
                  disabled={loading}
                  searchable
                  size="md"
                />

                <Select
                  label="Rep Rank"
                  placeholder="Select rep rank..."
                  data={repTypes.map(repType => ({
                    value: repType.id.toString(),
                    label: repType.name,
                  }))}
                  value={repData.repRank}
                  onChange={value =>
                    handleInputChange({
                      target: { name: "repRank", value: value || "" },
                    })
                  }
                  required
                  disabled={loading}
                  searchable
                  size="md"
                />

                {toggleRow("Is Manager?", repData.isManager, checked =>
                  handleInputChange({
                    target: {
                      name: "isManager",
                      checked,
                      type: "checkbox",
                    },
                  })
                )}
              </Stack>
            </Paper>

            {/* Optional Details — web-only fields (e.g. the Recruiting flow
                prefills phone) that iOS doesn't collect here. */}
            <Paper shadow="sm" p="md" radius="md">
              <Group
                gap="md"
                style={{ cursor: "pointer" }}
                justify="space-between"
                onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
              >
                <Title order={4}>Optional Details</Title>
                <ActionIcon variant="transparent" size="sm">
                  {showAdditionalDetails ? (
                    <IconChevronUp size={20} />
                  ) : (
                    <IconChevronDown size={20} />
                  )}
                </ActionIcon>
              </Group>

              <Collapse in={showAdditionalDetails}>
                <Stack gap="md" mt="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Phone Number"
                        placeholder="(000) 000-0000"
                        value={repData.phoneNumber}
                        onChange={event =>
                          handleInputChange({
                            target: {
                              name: "phoneNumber",
                              value: event.currentTarget.value,
                            },
                          })
                        }
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        type="date"
                        label="Birthday"
                        value={repData.birthday}
                        onChange={event =>
                          handleInputChange({
                            target: {
                              name: "birthday",
                              value: event.currentTarget.value,
                            },
                          })
                        }
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Instagram"
                        placeholder="@username or profile URL"
                        value={repData.instagram}
                        onChange={event =>
                          handleInputChange({
                            target: {
                              name: "instagram",
                              value: event.currentTarget.value,
                            },
                          })
                        }
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="LinkedIn"
                        placeholder="LinkedIn profile URL"
                        value={repData.linkedin}
                        onChange={event =>
                          handleInputChange({
                            target: {
                              name: "linkedin",
                              value: event.currentTarget.value,
                            },
                          })
                        }
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                  </Grid>

                  <Select
                    label="Recruiting Source"
                    placeholder="Select recruiting source..."
                    data={recruitingSources.map(source => ({
                      value: source.id.toString(),
                      label: source.source_name,
                    }))}
                    value={repData.recruitingSource}
                    onChange={value =>
                      handleInputChange({
                        target: {
                          name: "recruitingSource",
                          value: value || "",
                        },
                      })
                    }
                    disabled={loading}
                    clearable
                    size="md"
                  />

                  <Textarea
                    label="Notes"
                    placeholder="Enter any additional notes about this rep..."
                    rows={4}
                    value={repData.notes}
                    onChange={event =>
                      handleInputChange({
                        target: {
                          name: "notes",
                          value: event.currentTarget.value,
                        },
                      })
                    }
                    disabled={loading}
                  />
                </Stack>
              </Collapse>
            </Paper>

            {/* Documents — held locally and uploaded to the new rep's profile
                after the rep is created. */}
            <Paper shadow="sm" p="md" radius="md">
              <Stack gap="md">
                <Title order={3}>Documents</Title>
                <Text size="sm" c="dimmed">
                  Attach offer letters, comp agreements, or other paperwork.
                  They'll be saved to the rep's profile when the rep is created.
                </Text>
                <Box
                  component="label"
                  onDragOver={event => {
                    event.preventDefault();
                    setDocDragActive(true);
                  }}
                  onDragLeave={() => setDocDragActive(false)}
                  onDrop={event => {
                    event.preventDefault();
                    setDocDragActive(false);
                    if (loading) return;
                    const dropped = Array.from(event.dataTransfer?.files || []);
                    if (dropped.length > 0) {
                      setDocFiles(prev => [...prev, ...dropped]);
                    }
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    padding: "28px 16px",
                    border: `2px dashed ${docDragActive ? "#d0521f" : "#d1d5db"}`,
                    borderRadius: "10px",
                    background: docDragActive ? "#fff7f2" : "#fafafa",
                    cursor: loading ? "not-allowed" : "pointer",
                    transition:
                      "border-color 120ms ease, background 120ms ease",
                  }}
                >
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.zip,application/pdf"
                    multiple
                    disabled={loading}
                    onChange={event => {
                      const picked = Array.from(event.target.files || []);
                      if (picked.length > 0) {
                        setDocFiles(prev => [...prev, ...picked]);
                      }
                      event.target.value = "";
                    }}
                    style={{ display: "none" }}
                  />
                  <IconCloudUpload
                    size={30}
                    stroke={1.5}
                    color={docDragActive ? "#d0521f" : "#9ca3af"}
                  />
                  <Text size="sm" fw={600} c={docDragActive ? "#d0521f" : ""}>
                    Drag &amp; drop files here, or click to browse
                  </Text>
                  <Text size="xs" c="dimmed">
                    PDF, images, or ZIP
                  </Text>
                </Box>
                {docFiles.length > 0 && (
                  <Stack gap="xs">
                    {docFiles.map((file, index) => (
                      <Group
                        key={`${file.name}-${index}`}
                        justify="space-between"
                        wrap="nowrap"
                        px="sm"
                        py={8}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          background: "white",
                        }}
                      >
                        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                          <Box
                            style={{
                              width: "34px",
                              height: "34px",
                              flexShrink: 0,
                              background: "#fff1ea",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#d0521f",
                              letterSpacing: "0.03em",
                            }}
                          >
                            {(file.name.split(".").pop() || "FILE")
                              .slice(0, 4)
                              .toUpperCase()}
                          </Box>
                          <Box style={{ minWidth: 0 }}>
                            <Text size="sm" fw={500} truncate>
                              {file.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {file.size >= 1024 * 1024
                                ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                                : `${Math.max(1, Math.round(file.size / 1024))} KB`}
                            </Text>
                          </Box>
                        </Group>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          disabled={loading}
                          onClick={() =>
                            setDocFiles(prev =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          aria-label={`Remove ${file.name}`}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Paper>

            {/* Card 2: Pay */}
            <Paper shadow="sm" p="md" radius="md">
              <Stack gap="md">
                <Title order={3}>Pay</Title>

                {templateSelect(
                  "Use Default Scale Template",
                  commissionTemplates,
                  selectedTemplates.commission,
                  applyTemplate(
                    commissionTemplates,
                    "commissionLines",
                    "commission"
                  )
                )}
                <ScaleTable
                  title="Commission Scale"
                  col2Label="Commission"
                  lines={pay.commissionLines}
                  onChange={lines => patchPay({ commissionLines: lines })}
                />

                {toggleRow("Allow Override", pay.allowOverride, checked =>
                  patchPay({ allowOverride: checked })
                )}
                {pay.allowOverride && (
                  <>
                    {templateSelect(
                      "Use Default Override Template",
                      overrideTemplates,
                      selectedTemplates.override,
                      applyTemplate(
                        overrideTemplates,
                        "overrideLines",
                        "override"
                      )
                    )}
                    <ScaleTable
                      title="Override Scale"
                      col2Label="Override"
                      lines={pay.overrideLines}
                      onChange={lines => patchPay({ overrideLines: lines })}
                    />
                  </>
                )}

                {toggleRow("Marketing Deal", pay.hasMarketingDeal, checked =>
                  patchPay({ hasMarketingDeal: checked })
                )}
                {pay.hasMarketingDeal && (
                  <>
                    {templateSelect(
                      "Use Default Marketing Template",
                      marketingTemplates,
                      selectedTemplates.md,
                      applyTemplate(marketingTemplates, "mdLines", "md")
                    )}
                    <ScaleTable
                      title="MD Scale"
                      col2Label="MD"
                      lines={pay.mdLines}
                      onChange={lines => patchPay({ mdLines: lines })}
                    />
                  </>
                )}

                {/* One upfront pay per rep, so the add footer goes away once
                    set. */}
                <ItemTable
                  title="Upfront Pay"
                  addLabel="Add Upfront Pay"
                  empty={!pay.upfront}
                  onAdd={pay.upfront ? null : () => setAddTarget("upfront")}
                  form={
                    addTarget === "upfront" ? (
                      <AddUpfrontPayForm
                        companyUpfrontTypes={overview?.ps_company_upfront_types}
                        onCreated={created => {
                          patchPay({ upfront: created });
                          setAddTarget(null);
                        }}
                        onCancel={() => setAddTarget(null)}
                      />
                    ) : null
                  }
                >
                  {pay.upfront && (
                    <ItemRow
                      title={
                        UPFRONT_PAY_TYPE_LABELS[pay.upfront.upfront_pay_type] ||
                        "Upfront Pay"
                      }
                      fields={upfrontFields(pay.upfront, next =>
                        patchPay({ upfront: next })
                      )}
                      onRemove={() => patchPay({ upfront: null })}
                    />
                  )}
                </ItemTable>

                <ItemTable
                  title="Bonuses"
                  addLabel="Add Bonus"
                  empty={pay.bonuses.length === 0}
                  onAdd={() => setAddTarget("bonus")}
                  form={
                    addTarget === "bonus" ? (
                      <AddBonusForm
                        inline
                        sellingYearId={null}
                        companyBonusTypes={overview?.ps_company_bonus_types}
                        onCreated={created => {
                          patchPay({
                            bonuses: [
                              ...pay.bonuses,
                              { _id: nextEditorItemId(), ...created },
                            ],
                          });
                          setAddTarget(null);
                        }}
                        onCancel={() => setAddTarget(null)}
                      />
                    ) : null
                  }
                >
                  {pay.bonuses.map(bonus => (
                    <ItemRow
                      key={bonus._id}
                      title={bonus.name || "Bonus"}
                      subtitle={
                        BONUS_CATEGORY_LABELS[bonus.bonus_type_category] ||
                        titleize(bonus.bonus_type_category)
                      }
                      fields={bonusFields(bonus, next =>
                        patchPay({
                          bonuses: pay.bonuses.map(b =>
                            b._id === bonus._id
                              ? { ...next, _id: bonus._id }
                              : b
                          ),
                        })
                      )}
                      onRemove={() =>
                        patchPay({
                          bonuses: pay.bonuses.filter(b => b._id !== bonus._id),
                        })
                      }
                    />
                  ))}
                </ItemTable>

                <ItemTable
                  title="Expenses"
                  addLabel="Add Expense"
                  empty={pay.expenses.length === 0}
                  onAdd={() => setAddTarget("expense")}
                  form={
                    addTarget === "expense" ? (
                      <AddExpenseForm
                        inline
                        sellingYearId={null}
                        companyExpenseTypes={overview?.ps_company_expense_types}
                        onCreated={created => {
                          patchPay({
                            expenses: [
                              ...pay.expenses,
                              { _id: nextEditorItemId(), ...created },
                            ],
                          });
                          setAddTarget(null);
                        }}
                        onCancel={() => setAddTarget(null)}
                      />
                    ) : null
                  }
                >
                  {pay.expenses.map(expense => (
                    <ItemRow
                      key={expense._id}
                      title={expense.name || "Expense"}
                      subtitle={
                        EXPENSE_CATEGORY_LABELS[
                          expense.expense_type_category
                        ] || titleize(expense.expense_type_category)
                      }
                      fields={expenseFields(expense, next =>
                        patchPay({
                          expenses: pay.expenses.map(e =>
                            e._id === expense._id
                              ? { ...next, _id: expense._id }
                              : e
                          ),
                        })
                      )}
                      onRemove={() =>
                        patchPay({
                          expenses: pay.expenses.filter(
                            e => e._id !== expense._id
                          ),
                        })
                      }
                    />
                  ))}
                </ItemTable>
              </Stack>
            </Paper>

            {/* Footer buttons */}
            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={handleCancelClick}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading} loading={loading}>
                Add Rep
              </Button>
            </Group>
          </Stack>
        </Container>
      </Box>

      {/* Error message */}
      {error && <DisplayMessage type="error" message={error} />}

      {/* Confirmation Modal */}
      <Modal
        opened={showCancelConfirmation}
        onClose={handleCancelConfirmation}
        title="Cancel Adding Rep?"
        centered
      >
        <Stack gap="md">
          <Text>All entered data will be lost.</Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={handleCancelConfirmation}>
              Continue Editing
            </Button>
            <Button onClick={handleConfirmCancel}>Yes, Cancel</Button>
          </Group>
        </Stack>
      </Modal>
    </PageContent>
  );
}

export default AddRepPage;
